import { useBreakpointValue } from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { match } from 'ts-pattern';
import { useContextSelector } from 'use-context-selector';
import wrapColorTheme from '../../lib/WrapColorTheme';
import { colors, styled } from '../../Stitches';
import EditorContext from './EditorContext';
import Emoji from '../Emoji';
import { EditorValueState } from './useEditorMachine';
import useLocalStorage from '../hooks/useLocalStorage';

const Badge = wrapColorTheme(
  styled('div', {
    width: 'fit-content',
    padding: '$3',
    margin: 0,

    fontFamily: '$mono',
    fontWeight: 'bold',
    fontSize: '$1',
    textTransform: 'uppercase',

    borderRadius: '$3',

    transitionProperty: 'all',
    transitionDuration: '120ms',

    cursor: 'pointer',

    variants: {
      theme: {
        light: {
          '&:hover': { backgroundColor: colors.gray100 },
          '&:active': { backgroundColor: colors.gray300 },
        },
        dark: {
          '&:hover': { backgroundColor: colors.dark.whiteAlpha300 },
          '&:active': { backgroundColor: colors.dark.whiteAlpha400 },
        },
      },
    },
  })
);

const BadgeWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  marginTop: '$5',

  userSelect: 'none',

  variants: { offset: { true: { marginBottom: -12 } } },
});

const BadgeLabel = styled(motion.div, {
  marginLeft: '$3',
  display: 'inline-block',
  verticalAlign: 'top',
  whiteSpace: 'nowrap',
  textOverflow: 'clip',
  overflow: 'hidden',
});

const OuterBadge = ({ children, onClick }: { children: React.ReactNode; onClick: () => void }) => {
  const offset = useBreakpointValue({ base: undefined, md: true });

  return (
    <BadgeWrapper offset={offset}>
      <Badge onClick={onClick}>{children}</Badge>
    </BadgeWrapper>
  );
};

type SyncBadgeProps = { local?: boolean; state: EditorValueState };

const editorStateToEmoji = ({ value }: EditorValueState, local?: boolean) => {
  const savedEmoji = local ? (
    <Emoji symbol="💾" label="Emoji of a floppy disk" />
  ) : (
    <Emoji symbol="✅" label="Emoji of a tick check mark" />
  );

  const warningEmoji = <Emoji spaceRight symbol="⚠️" label="Emoji of an warning sign" />;

  return match(value)
    .with('fetching', () => <Emoji symbol="🚛" />)
    .with('editing', () => <Emoji symbol="✍️" label="Emoji of a hand writing with a pen" />)
    .with('syncing', () => <Emoji spaceRight symbol="🌀" label="Emoji of a swirl" />)
    .with('syncError', () => warningEmoji)
    .with('fetchError', () => warningEmoji)
    .with('saved', () => savedEmoji)
    .with('idling', () => savedEmoji)
    .with('reloading', () => <Emoji symbol="⏱" label="Emoji of stopwatch" />)
    .exhaustive();
};

const editorStateToLabel = ({ value }: EditorValueState, local?: boolean): string =>
  match(value)
    .with('fetching', () => 'loading')
    .with('fetchError', () => 'Error')
    .with('syncError', () => 'Error')
    .with('editing', () => 'Editing')
    .with('syncing', () => 'syncing')
    .with('saved', () => (local ? 'saved locally' : 'draft saved'))
    .with('idling', () => (local ? 'saved locally' : 'draft saved'))
    .with('reloading', () => 'reloading')
    .exhaustive();

type SyncBadgeStyle = 'normal' | 'minimal';

const useSyncBadge = () => {
  const cycleSyncBadgeStyle = (style: SyncBadgeStyle): SyncBadgeStyle =>
    match(style)
      .with('normal', () => 'minimal')
      .with('minimal', () => 'normal')
      .exhaustive() as SyncBadgeStyle;

  const [style, setStyle] = useLocalStorage<SyncBadgeStyle>('sync-badge-style', 'normal');

  return { style, cycleStyle: () => setStyle(cycleSyncBadgeStyle(style)) };
};

const badgeLabelMotionProps = {
  key: 'label',
  transition: { type: 'spring', stiffness: 1000, damping: 100 },
  initial: { width: 0, opacity: 0 },
  exit: { width: 0, opacity: 0, margin: 0 },
  animate: { width: 'fit-content', opacity: 1 },
};

export const SyncBadge = ({ state, local }: SyncBadgeProps) => {
  const { style, cycleStyle } = useSyncBadge();
  return (
    <OuterBadge onClick={cycleStyle}>
      <AnimatePresence mode="sync">
        {editorStateToEmoji(state, local)}

        {style === 'normal' && (
          <BadgeLabel {...badgeLabelMotionProps}>{editorStateToLabel(state, local)}</BadgeLabel>
        )}
      </AnimatePresence>
    </OuterBadge>
  );
};

export const NewSyncBadge = () => {
  const stateMachine = useContextSelector(EditorContext, (ctx) => ctx.stateMachine);
  const { active } = useContextSelector(EditorContext, (ctx) => ctx.localBackup);

  return <SyncBadge state={stateMachine.state as EditorValueState} local={active} />;
};
